import { useState, useEffect } from 'react';
import { RouterProvider, createBrowserRouter, Navigate, Link, Outlet, useLocation } from 'react-router-dom';
import { useSecurityAlerts } from './hooks/useSecurityAlerts';
import { I18nProvider } from './i18n';
import AlertBanner from './components/security/AlertBanner';
import Navigation from './components/layout/Navigation';
import Breadcrumb from './components/layout/Breadcrumb';
import ProtectedRoute from './components/routing/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import SsoPage from './pages/SsoPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import { isAllowedOnThisPortal, PORTAL_BRAND } from './config/institution';
import { authService } from './services/authService';
import MessagesPage from './pages/MessagesPage';
import GuestDetailPage from './pages/GuestDetailPage';
import SecurityAlertsPage from './pages/SecurityAlertsPage';
import WatchlistPage from './pages/WatchlistPage';
import ScoringRulesPage from './pages/ScoringRulesPage';
import RiskScenariosPage from './pages/RiskScenariosPage';
import StatisticsPage from './pages/StatisticsPage';
import GuestsListPage from './pages/GuestsListPage';
import DocumentValidationPage from './pages/DocumentValidationPage';
import TaxConfigurationPage from './pages/admin/TaxConfigurationPage';
import OperationalDashboardPage from './pages/OperationalDashboardPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import HotelManagementPage from './pages/admin/HotelManagementPage';
import GlobalDashboardPage from './pages/admin/GlobalDashboardPage';
import HotelOnboardingPage from './pages/admin/HotelOnboardingPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import IncidentsPage from './pages/IncidentsPage';
import CreateIncidentPage from './pages/CreateIncidentPage';
import IncidentDetailPage from './pages/IncidentDetailPage';
import AuditLogsPage from './pages/AuditLogsPage';
import HealthMonitoringPage from './pages/HealthMonitoringPage';
import RegulationDetailPage from './pages/RegulationDetailPage';
import RegulationFormPage from './pages/RegulationFormPage';
import RelationshipAnalysisPage from './pages/RelationshipAnalysisPage';
import ImportMonitoringPage from './pages/admin/ImportMonitoringPage';
import InvestigationSearchPage from './pages/InvestigationSearchPage';
import InvestigationPersonDetailPage from './pages/InvestigationPersonDetailPage';
import InvestigationGeoPage from './pages/InvestigationGeoPage';
import MatchAlertsPage from './pages/MatchAlertsPage';
import { WS_URL } from './config/apiConfig';

// Composant HomePage
function HomePage() {
  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">CNS Dashboard</h1>
          <p className="text-gray-600">National Security Monitoring System</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Module 1: Messages */}
          <Link to="/messages" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-blue-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">💬 Messages</h3>
            <p className="text-gray-600 text-sm">Messagerie sécurisée</p>
          </Link>

          {/* Module 2: Security Alerts */}
          <Link to="/alerts" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-green-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">🛡️ Alertes de sécurité</h3>
            <p className="text-gray-600 text-sm">Notifications en temps réel</p>
          </Link>

          {/* Module 3: Guest Records */}
          <Link to="/guests" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-purple-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">👤 Dossiers invités</h3>
            <p className="text-gray-600 text-sm">Scores et historiques</p>
          </Link>

          {/* Module 4: Scoring Rules */}
          <Link to="/scoring-rules" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-orange-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">⚖️ Règles de scoring</h3>
            <p className="text-gray-600 text-sm">Configuration des scores de risque</p>
          </Link>

          {/* Module 5: Risk Scenarios */}
          <Link to="/scenarios" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-red-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">⚠️ Scénarios de risque</h3>
            <p className="text-gray-600 text-sm">Gestion des scénarios prédéfinis</p>
          </Link>

          {/* Module 6: Statistics */}
          <Link to="/statistics" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-indigo-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">📊 Statistiques</h3>
            <p className="text-gray-600 text-sm">Tableaux de bord et rapports</p>
          </Link>

          {/* Module 7: Document Validation */}
          <Link to="/documents" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-teal-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">📄 Validation documents</h3>
            <p className="text-gray-600 text-sm">Vérification passeports et visas</p>
          </Link>

          {/* Module 8: Watchlist Management */}
          <Link to="/watchlist" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-pink-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">👁️ Listes de surveillance</h3>
            <p className="text-gray-600 text-sm">Gestion des personnes surveillées</p>
          </Link>

          {/* Module 9: Investigation */}
          <Link to="/investigation/search" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
            <div className="text-cyan-600 mb-4">
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">🔍 Investigation</h3>
            <p className="text-gray-600 text-sm">Recherche et analyse d'identités</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Composant Layout avec navigation et alertes
function AppLayout() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();
  const { activeAlerts, acknowledgeAlert, dismissAlert } = useSecurityAlerts(isAuthenticated ? WS_URL : undefined);

  useEffect(() => {
    import('./services/authService')
      .then(({ authService }) => {
        setIsAuthenticated(authService.isAuthenticated());
      })
      .catch((error) => {
        console.error('Failed to load authService:', error);
        setIsAuthenticated(false);
      });
  }, []);

  const handleLogout = () => {
    import('./services/authService')
      .then(({ authService }) => {
        authService.logout();
        setIsAuthenticated(false);
        window.location.href = '/login';
      })
      .catch((error) => {
        console.error('Failed to logout:', error);
        window.location.href = '/login';
      });
  };

  // Wait for auth check to complete
  if (isAuthenticated === null) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center text-gray-500">Loading...</div>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Institution isolation: reject tokens from the other security agency
  const portalUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  })();
  if (!isAllowedOnThisPortal(portalUser)) {
    authService.logout();
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Security Alert Banners */}
      <div className="fixed top-0 right-0 z-50 space-y-4 p-4">
        {activeAlerts.map((alert, index) => (
          <div key={alert.id} style={{ marginTop: `${index * 20}px` }}>
            <AlertBanner
              alert={alert}
              guestName={alert.guest_name}
              guestPhoto={alert.guest_photo}
              hotelName={alert.hotel_name}
              riskFactors={alert.risk_factors}
              onAcknowledge={() => acknowledgeAlert(alert.id)}
              onDismiss={() => dismissAlert(alert.id)}
            />
          </div>
        ))}
      </div>

      {/* Navigation */}
      <Navigation onLogout={handleLogout} />

      {/* Breadcrumb */}
      <Breadcrumb />

      {/* Main Content - Outlet renders child routes */}
      <Outlet />
    </div>
  );
}

// Configuration du router avec React Router v7 future flags
const router = createBrowserRouter(
  [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/sso',
      element: <SsoPage />,
    },
    {
      path: '/',
      element: <AppLayout />,
      children: [
        {
          index: true,
          element: <HomePage />,
        },
        {
          path: 'change-password',
          element: <ChangePasswordPage />,
        },
        {
          path: 'messages',
          element: <MessagesPage />,
        },
        {
          path: 'alerts',
          element: <SecurityAlertsPage />,
        },
        {
          path: 'watchlist',
          element: <WatchlistPage />,
        },
        {
          path: 'scoring-rules',
          element: <ScoringRulesPage />,
        },
        {
          path: 'scenarios',
          element: <RiskScenariosPage />,
        },
        {
          path: 'statistics',
          element: <StatisticsPage />,
        },
        {
          path: 'guests',
          element: <GuestsListPage />,
        },
        {
          path: 'guests/:id',
          element: <GuestDetailPage />,
        },
        {
          path: 'documents',
          element: <DocumentValidationPage />,
        },
        {
          path: 'incidents',
          element: <IncidentsPage />,
        },
        {
          path: 'incidents/new',
          element: <CreateIncidentPage />,
        },
        {
          path: 'incidents/:id',
          element: <IncidentDetailPage />,
        },
        {
          path: 'admin/hotels',
          element: (
            <ProtectedRoute permission="HOTELS_VIEW" fallbackPath="/access-denied">
              <HotelManagementPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'admin/dashboard',
          element: (
            <ProtectedRoute permission="HOTELS_VIEW" fallbackPath="/access-denied">
              <GlobalDashboardPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'admin/hotels/pending',
          element: (
            <ProtectedRoute permission="HOTELS_VIEW" fallbackPath="/access-denied">
              <HotelOnboardingPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'admin/users',
          element: (
            <ProtectedRoute permission="USERS_VIEW" fallbackPath="/access-denied">
              <UserManagementPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'tax-config',
          element: (
            <ProtectedRoute permission="TAX_VIEW" fallbackPath="/access-denied">
              <TaxConfigurationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'admin/settings',
          element: (
            <ProtectedRoute permission="SETTINGS_VIEW" fallbackPath="/access-denied">
              <SystemSettingsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'operational',
          element: (
            <ProtectedRoute permission="OPERATIONAL_DASHBOARD" fallbackPath="/access-denied">
              <OperationalDashboardPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'audit-logs',
          element: (
            <ProtectedRoute permission="AUDIT_VIEW" fallbackPath="/access-denied">
              <AuditLogsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'health-monitoring',
          element: (
            <ProtectedRoute permission="HEALTH_VIEW" fallbackPath="/access-denied">
              <HealthMonitoringPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'regulations',
          element: (
            <ProtectedRoute permission="REGULATIONS_VIEW" fallbackPath="/access-denied">
              <RegulationFormPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'regulations/new',
          element: (
            <ProtectedRoute permission="REGULATIONS_VIEW" fallbackPath="/access-denied">
              <RegulationFormPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'regulations/:id',
          element: (
            <ProtectedRoute permission="REGULATIONS_VIEW" fallbackPath="/access-denied">
              <RegulationDetailPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'relationships',
          element: (
            <ProtectedRoute permission="RELATIONSHIPS_VIEW" fallbackPath="/access-denied">
              <RelationshipAnalysisPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'admin/import-monitoring',
          element: (
            <ProtectedRoute permission="HOTELS_VIEW" fallbackPath="/access-denied">
              <ImportMonitoringPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'investigation/search',
          element: (
            <ProtectedRoute permission="INVESTIGATION_SEARCH" fallbackPath="/access-denied">
              <InvestigationSearchPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'investigation/persons/:id',
          element: (
            <ProtectedRoute permission="INVESTIGATION_PERSON_READ" fallbackPath="/access-denied">
              <InvestigationPersonDetailPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'investigation/geo',
          element: (
            <ProtectedRoute permission="INVESTIGATION_GEO_READ" fallbackPath="/access-denied">
              <InvestigationGeoPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'investigation/alerts',
          element: (
            <ProtectedRoute permission="INVESTIGATION_MATCH_READ" fallbackPath="/access-denied">
              <MatchAlertsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'access-denied',
          element: <AccessDeniedPage />,
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/login" replace />,
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  } as any // Type cast pour supporter les future flags v7
);

function App() {
  return (
    <I18nProvider>
      <RouterProvider router={router} />
    </I18nProvider>
  );
}

export default App;
